'use client';

import { Component, type ReactNode } from 'react';

/**
 * A `ChunkLoadError` means the browser failed to download a lazily-imported
 * (`next/dynamic`) JS chunk from the CDN — almost always because a deploy
 * rotated the build the page is running against, so the chunk at the build's
 * path isn't reachable in that moment (propagation window, edge miss, or the
 * client is on the outgoing build). It is transient and recoverable: a fresh
 * document fetch pulls a consistent build and the chunk resolves.
 *
 * Without this boundary, that failure throws through `<Suspense>` into the
 * app-wide `RpErrorBoundary`, which renders the dead "Oops! Something went
 * wrong" page with no path back — a recoverable blip becomes a hard, sticky
 * crash for the user. See HP-8213.
 *
 * Why this app is especially exposed: App Router does client-side `<Link>`
 * navigation with background route prefetching, plus per-route-segment and
 * `next/dynamic` code-splitting, so a long-lived session keeps fetching
 * build-specific chunks for its whole lifetime. When a deploy lands
 * mid-session the build the page booted with goes stale and the next
 * (pre)fetch 404s — observed in production when a hotpads deploy rotated the
 * build out from under tabs that were already open (HP-8213).
 *
 * This boundary sits INSIDE `RpErrorBoundary` (so it catches first) and ONLY
 * handles chunk-load errors: it reloads the page once to recover. Every other
 * error is re-thrown from `render` so the outer `RpErrorBoundary` handles it
 * exactly as before.
 */

const RELOAD_GUARD_KEY = 'hp-chunk-reload';

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // webpack throws `ChunkLoadError`; match the message too in case the name is
  // lost across an error re-wrap.
  return error.name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/i.test(error.message);
}

function hasAlreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
  } catch {
    // sessionStorage can throw (private mode, disabled storage). Treat as
    // "not reloaded yet" so recovery still gets one attempt.
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch {
    // Best effort — if we can't persist the guard we accept the small risk of
    // a second reload rather than blocking recovery.
  }
}

/**
 * Clear the one-shot reload guard. Call on a clean app load so a chunk failure
 * later in the same session (e.g. on a subsequent navigation) can recover too.
 */
export function clearChunkReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // ignore
  }
}

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ChunkLoadRecoveryBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    if (!isChunkLoadError(error)) return;
    if (typeof window === 'undefined') return;
    if (hasAlreadyReloaded()) return; // already retried once → don't loop
    markReloaded();
    window.location.reload();
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      // Recoverable chunk error we haven't already retried: render nothing
      // while the reload kicks in, so the user never sees the "Oops" flash.
      if (isChunkLoadError(error) && !hasAlreadyReloaded()) {
        return null;
      }
      // Anything else — including a chunk error that already survived a reload
      // (asset genuinely missing) — bubbles to the outer RpErrorBoundary.
      throw error;
    }
    return this.props.children;
  }
}
